import { useUserEventLogger } from '@repo/tracking-service';
import { Alert, AlertTitle, BoxButton, Card, CardContent, Divider, List, StickyBottomWrapper, SubTitle, Svg, TextList } from '@repo/ui';
import { Title } from '@zenterprise-inc/ui';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';

import { COMMON_PATHS } from '@/lib/constants/paths';
import { usePaymentCard } from '@/lib/hooks/use-payment-card';

export const PaymentCardContent = () => {
  const router = useRouter();
  const { sendViewEvent, sendClickEvent } = useUserEventLogger('card-register');
  const { hasRegisteredCard, firstCard, cardName, cardLogo, cardNumber, fetchCardData } = usePaymentCard();
  const expireYear = firstCard?.expireYear;
  const expireMonth = firstCard?.expireMonth;

  sendViewEvent('card-register');

  const isCardExpiringSoon = useMemo(() => {
    if (!expireMonth || !expireYear) return false;

    const month = parseInt(expireMonth);
    const year = parseInt(expireYear);

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear() % 100;
    const currentMonth = currentDate.getMonth() + 1;

    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      return false;
    }

    const cardExpiryDate = new Date(2000 + year, month, 0);

    const ninetyDaysBeforeExpiry = new Date(cardExpiryDate);
    ninetyDaysBeforeExpiry.setDate(cardExpiryDate.getDate() - 90);

    return currentDate > ninetyDaysBeforeExpiry;
  }, [expireMonth, expireYear]);

  if (isCardExpiringSoon) {
    sendViewEvent('card-register-alert');
  }

  useEffect(() => {
    fetchCardData();
  }, [fetchCardData]);

  const handleCardClick = () => {
    sendClickEvent(hasRegisteredCard ? `card-register${isCardExpiringSoon ? '-alert' : ''}_edit` : 'card-register_create');
    router.push(COMMON_PATHS.환급결제카드입력);
  };

  const handleCtaClick = () => {
    sendClickEvent('card-register_confirm');
    if (router.query.fromLookup) {
      const newQuery = { ...router.query };
      delete newQuery.fromLookup;

      router.push({
        pathname: COMMON_PATHS.환급계좌입력,
        query: newQuery
      });
      return;
    }
    router.back();
  };

  return (
    <div className="page-padding flex w-full flex-col pb-24">
      <Title.Headline className="py-[24px]" size="large" subText={`나중에 번거로움 없이 자동 결제돼요.`} title={`환급 완료 후 결제될\n카드를 등록해주세요`} />
      <SubTitle title={'결제 카드'} />
      {hasRegisteredCard ? (
        <List
          title={cardName}
          className={'p-normal-medium'}
          description={cardNumber}
          leadingElement={<Svg src={cardLogo ?? ''} width={32} height={32} />}
          supportingElement={
            <BoxButton size={'small'} variant={'tonal-primary'} onClick={handleCardClick}>
              변경
            </BoxButton>
          }
        />
      ) : (
        <Card className={'mb-normal-medium cursor-pointer'} onClick={handleCardClick}>
          <CardContent>
            <div className={'flex flex-col gap-tiny-large items-center'}>
              <Svg width={40} height={40} src={'icg_plus_md.svg'} />
              <div className={'body-small-semibold text-neutral-foreground-main'}>카드 등록하기</div>
            </div>
          </CardContent>
        </Card>
      )}
      {hasRegisteredCard && isCardExpiringSoon && (
        <Alert variant={'warning'} size={'medium'}>
          <AlertTitle>등록된 카드의 유효기간이 곧 만료돼요. 다른 카드로 변경해주세요.</AlertTitle>
        </Alert>
      )}
      {hasRegisteredCard && <Divider className={'mt-normal-large mb-normal-small'} />}
      <SubTitle title={'확인해주세요'} />
      <TextList className={'text-neutral-foreground-low'}>고객님이 환급받기 전에는 결제되지 않아요.</TextList>
      <TextList className={'text-neutral-foreground-low'}>국세청에서 결정된 환급금의 30%예요.(부가세별도)</TextList>
      <TextList className={'text-neutral-foreground-low'}>체납액이나 예정 납부액이 있다면 자동 충당되어 더 적게 입금될 수 있어요.</TextList>
      <StickyBottomWrapper>
        <BoxButton className={'w-full'} size={'large'} disabled={!(hasRegisteredCard && !isCardExpiringSoon)} onClick={handleCtaClick}>
          {!hasRegisteredCard ? '카드 등록이 필요해요' : isCardExpiringSoon ? '다른 카드로 변경이 필요해요' : '등록 완료'}
        </BoxButton>
      </StickyBottomWrapper>
    </div>
  );
};
